'use client';

import { useEffect, useRef, useState } from 'react';

import { Clock } from 'lucide-react';

import { useMappingMemorySearch, transformToSuggestion } from '../api/use-mapping-memory';
import type { MappingMemorySuggestion } from '../types/mapping-memory';

import { Textarea } from '@/components/ui/textarea';

interface ActivityDescriptionAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelectSuggestion: (suggestion: MappingMemorySuggestion) => void;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  /** Unit type filter for mapping memory search (Energy, Weight, etc.) */
  unitType?: string;
}

export function ActivityDescriptionAutocomplete({
  value,
  onChange,
  onSelectSuggestion,
  placeholder,
  disabled,
  rows = 3,
  unitType,
}: ActivityDescriptionAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce value for search
  const [debouncedValue, setDebouncedValue] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => { setDebouncedValue(value); }, 300);
    return () => { clearTimeout(timer); };
  }, [value]);

  const { data: searchData } = useMappingMemorySearch(debouncedValue, { unitType, limit: 5 });
  const suggestions: MappingMemorySuggestion[] = searchData?.items
    ? searchData.items.map(transformToSuggestion)
    : [];

  useEffect(() => {
    setOpen(suggestions.length > 0 && value.trim().length >= 2);
  }, [suggestions.length, value]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => { document.removeEventListener('mousedown', handleClick); };
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <Textarea
        rows={rows}
        value={value}
        onChange={(e) => { onChange(e.target.value); }}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => {
          if (suggestions.length > 0) setOpen(true);
        }}
      />
      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-md border bg-popover p-1 shadow-md">
          <p className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-medium text-muted-foreground">
            <Clock className="h-3 w-3" />
            Recently used mappings
          </p>
          {suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              className="flex w-full flex-col items-start rounded px-2 py-1.5 text-left transition-colors hover:bg-accent"
              onClick={() => {
                onSelectSuggestion(s);
                setOpen(false);
              }}
            >
              <span className="text-sm font-medium">{s.description}</span>
              <span className="text-xs text-muted-foreground">
                {s.activityName} → {s.factorName}
                <span className="ml-2 font-mono">
                  {s.co2ePerUnit.toLocaleString('en', { maximumFractionDigits: 4 })} {s.co2eUnit}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
